import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import UniversalNavbar from '../../components/ui/UniversalNavbar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { billingService } from '../../services/billingService';
import { formatDateTime } from '../../utils/formatters';

const money = (n) => `${Number(n || 0).toLocaleString()} DZD`;

const BillingPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('billing');
  const { user } = useAuth();
  const [params] = useSearchParams();
  const conferenceId = params?.get('conferenceId');
  const intentPublish = params?.get('intent') === 'publish';

  const [billing, setBilling] = useState(null);
  const [plans, setPlans] = useState([]);
  const [settings, setSettings] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [receiptFile, setReceiptFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (!user) { navigate('/user-sign-in'); return; }
    load();
  }, [user]);

  const load = async () => {
    setLoading(true);
    try {
      const [b, p, s, hist] = await Promise.all([
        billingService.getMyBilling(),
        billingService.getPlans(),
        billingService.getPaymentSettings(),
        billingService.getMyPayments()
      ]);
      setBilling(b);
      setPlans(p);
      setSettings(s);
      setPayments(hist);
    } finally {
      setLoading(false);
    }
  };

  const annualActive = billing?.annualUntil && new Date(billing?.annualUntil) > new Date();
  const canPublish = annualActive || (billing?.freePublishesRemaining || 0) > 0 || (billing?.credits || 0) > 0;
  const hasPendingPayment = payments?.some(p => p?.status === 'pending');

  const handleSubmitPayment = async () => {
    if (!selectedPlan) return;
    setSubmitting(true);
    setFeedback(null);
    try {
      await billingService.submitPayment({
        planId: selectedPlan?.id,
        amountDzd: selectedPlan?.priceDzd,
        receiptFile
      });
      setSelectedPlan(null);
      setReceiptFile(null);
      setFeedback({ type: 'success', text: t('feedback.submitted') });
      await load();
    } catch (err) {
      setFeedback({ type: 'error', text: err?.message || t('feedback.submitFailed') });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePublishNow = async () => {
    setPublishing(true);
    setFeedback(null);
    try {
      await billingService.publishConference(conferenceId);
      navigate(`/conference-management-dashboard?conferenceId=${conferenceId}`);
    } catch (err) {
      if ((err?.message || '')?.includes('payment_required')) {
        setFeedback({ type: 'error', text: t('feedback.noEntitlement') });
        await load();
      } else {
        setFeedback({ type: 'error', text: err?.message || t('feedback.publishFailed') });
      }
    } finally {
      setPublishing(false);
    }
  };

  const statusBadge = (status) => ({
    pending: 'bg-warning/10 text-warning',
    approved: 'bg-success/10 text-success',
    rejected: 'bg-error/10 text-error'
  }[status] || 'bg-muted text-muted-foreground');

  return (
    <div className="min-h-screen bg-background">
      <UniversalNavbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-1">{t('title')}</h1>
        <p className="text-sm text-muted-foreground mb-6">
          <Trans i18nKey="subtitle" ns="billing" components={{ b: <span className="font-medium text-foreground" /> }} />
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Icon name="Loader2" size={36} className="text-primary animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Entitlement summary */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-xl border border-border bg-card p-4">
                <p className="text-xs text-muted-foreground">{t('freePublishesLeft')}</p>
                <p className="text-2xl font-bold text-foreground">{billing?.freePublishesRemaining ?? 0}</p>
              </div>
              <div className="rounded-xl border border-border bg-card p-4">
                <p className="text-xs text-muted-foreground">{t('conferenceCredits')}</p>
                <p className="text-2xl font-bold text-foreground">{billing?.credits ?? 0}</p>
              </div>
              <div className="rounded-xl border border-border bg-card p-4">
                <p className="text-xs text-muted-foreground">{t('annualPlan')}</p>
                <p className="text-sm font-semibold text-foreground mt-1.5">
                  {annualActive ? t('annualActiveUntil', { date: formatDateTime(billing?.annualUntil) }) : '—'}
                </p>
              </div>
            </div>

            {feedback && (
              <div className={`p-3 rounded-lg text-sm flex items-start gap-2 ${feedback?.type === 'error' ? 'bg-error/10 text-error' : 'bg-success/10 text-success'}`}>
                <Icon name={feedback?.type === 'error' ? 'AlertCircle' : 'CheckCircle'} size={16} className="flex-shrink-0 mt-0.5" />
                {feedback?.text}
              </div>
            )}

            {/* Publish intent banner */}
            {conferenceId && intentPublish && (
              <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
                {canPublish ? (
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <p className="text-sm font-semibold text-foreground">{t('readyToPublish')}</p>
                      <p className="text-xs text-muted-foreground">{t('readyToPublishDesc')}</p>
                    </div>
                    <Button onClick={handlePublishNow} loading={publishing} iconName="Send" iconPosition="left">
                      {t('publishNow')}
                    </Button>
                  </div>
                ) : hasPendingPayment ? (
                  <div className="flex items-center gap-2 text-sm text-foreground">
                    <Icon name="Clock" size={16} className="text-warning" />
                    {t('awaitingApproval')}
                  </div>
                ) : (
                  <p className="text-sm text-foreground">
                    {t('requiresPlan')}
                  </p>
                )}
              </div>
            )}

            {/* Plans */}
            {!canPublish || !intentPublish ? (
              <div>
                <h2 className="text-sm font-semibold text-foreground mb-3">{t('payOnlyForWhatYouNeed')}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {plans?.map((plan) => (
                    <button
                      key={plan?.id}
                      onClick={() => { setSelectedPlan(plan); setFeedback(null); }}
                      className={`text-start rounded-xl border p-4 transition-colors ${
                        selectedPlan?.id === plan?.id ? 'border-primary ring-2 ring-primary/20 bg-primary/5' : 'border-border bg-card hover:border-primary/40'
                      }`}
                    >
                      <p className="text-sm font-semibold text-foreground">{plan?.name}</p>
                      <p className="text-2xl font-extrabold text-primary mt-1">{money(plan?.priceDzd)}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {plan?.planType === 'annual'
                          ? t('planUnlimited', { count: plan?.durationDays || 365 })
                          : t('planConferences', { count: plan?.credits || 0 })}
                      </p>
                      {plan?.description && <p className="text-[11px] text-muted-foreground mt-2">{plan?.description}</p>}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {/* Payment panel */}
            {selectedPlan && (
              <div className="rounded-xl border border-border bg-card p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-foreground">{t('payFor', { name: selectedPlan?.name, price: money(selectedPlan?.priceDzd) })}</h3>
                  <button onClick={() => setSelectedPlan(null)} className="text-muted-foreground hover:text-foreground"><Icon name="X" size={16} /></button>
                </div>

                <div className="rounded-lg bg-muted/50 p-4 text-sm space-y-1.5">
                  <p className="font-medium text-foreground mb-1">{t('paymentInstructions')}</p>
                  {settings?.baridimobNumber && <p className="text-muted-foreground">Baridi Mob: <span className="font-mono text-foreground">{settings?.baridimobNumber}</span></p>}
                  {settings?.ccpNumber && <p className="text-muted-foreground">CCP: <span className="font-mono text-foreground">{settings?.ccpNumber}</span></p>}
                  {settings?.ripNumber && <p className="text-muted-foreground">RIP: <span className="font-mono text-foreground">{settings?.ripNumber}</span></p>}
                  {settings?.instructions && <p className="text-muted-foreground whitespace-pre-wrap mt-2">{settings?.instructions}</p>}
                  {!settings?.baridimobNumber && !settings?.ccpNumber && !settings?.ripNumber && !settings?.instructions && (
                    <p className="text-warning">{t('notConfigured')}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground mb-2">{t('uploadReceipt')}</label>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={(e) => setReceiptFile(e?.target?.files?.[0] || null)}
                    className="block w-full text-sm text-muted-foreground file:me-3 file:py-2 file:px-3 file:rounded-md file:border file:border-border file:bg-muted file:text-foreground file:text-sm"
                  />
                  {receiptFile && <p className="text-xs text-muted-foreground mt-1">{receiptFile?.name}</p>}
                </div>

                <div className="flex justify-end">
                  <Button onClick={handleSubmitPayment} loading={submitting} disabled={!receiptFile} iconName="Upload" iconPosition="left">
                    {t('submitForReview')}
                  </Button>
                </div>
              </div>
            )}

            {/* History */}
            {payments?.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-foreground mb-3">{t('paymentHistory')}</h2>
                <div className="space-y-2">
                  {payments?.map((p) => (
                    <div key={p?.id} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{p?.planName || t('planFallback')} · {money(p?.amountDzd)}</p>
                        <p className="text-[11px] text-muted-foreground">{formatDateTime(p?.createdAt)} · {p?.referenceCode}</p>
                        {p?.adminNote && <p className="text-[11px] text-error mt-0.5">{p?.adminNote}</p>}
                      </div>
                      <span className={`px-2 py-0.5 text-[11px] font-medium ${statusBadge(p?.status)}`}>{t(`status.${p?.status}`, p?.status)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default BillingPage;
